import React, { useEffect, useMemo, useRef, useState } from "react";
import { sentenceCaseLabel, useTranslation } from "../i18n";

import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAuthSessionAction,
  getVendorProfileAction,
  updateVendorProfileAction,
  uploadVendorProfileImageAction,
  vendorChangePasswordAction,
} from "../actions/auth";
import { backendUrl } from "../Router/constant";
import { countryCodes } from "../data/countryCodes";
import { CURRENCY_DROPDOWN_OPTIONS } from "../data/currencyOptions";
import bankingDetailsService from "../services/bankingDetails.service";
import CustomDropdown from "../Components/CustomDropdown";
import { toogleErrorMessage, toogleSuccessMessage } from "../actions/alertMessage";
import LoadingSpinner from "../Components/LoadingSpinner";
import {
  handleNumericInputKeyDown,
  sanitizeNumericInput,
} from "../utils/numericInput";
const PROFILE_STORAGE_KEY = "vendorProfile";
const PROFILE_IMAGE_STORAGE_KEY = "adminProfileImage";
const EMPTY_BANKING_DETAILS = {
  bankName: "",
  bankCountry: "",
  accountHolder: "",
  accountNumber: "",
  swiftBic: "",
  branch: "",
  currency: "",
  paymentInstructions: "",
};
const BANK_COUNTRY_OPTIONS = countryCodes.map((country) => ({
  value: country.code,
  label: country.name,
  selectedLabel: country.name,
  searchText: country.name + " " + country.code,
}));
const getBankingFieldText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value !== "object") return String(value);
  return String(
    value.name ||
      value.label ||
      value.value ||
      value.code ||
      value.countryCode ||
      value.currencyCode ||
      "",
  );
};
const mapBankingDetails = (vendor) => {
  const details =
    vendor?.bankingDetails ||
    vendor?.bankDetails ||
    vendor?.bankAccountDetails ||
    vendor?.bankInformation ||
    vendor?.companyProfile?.bankingDetails ||
    vendor?.companyProfile?.bankDetails ||
    (vendor?.bankName ||
    vendor?.bankCountry ||
    vendor?.accountNumber ||
    vendor?.accountNumberOrIban ||
    vendor?.iban ||
    vendor?.swiftBic ||
    vendor?.branch ||
    vendor?.paymentInstructions
      ? vendor
      : {});
  const rawCountry =
    details.bankCountry || details.bankCountryCode || details.country || "";
  const countryText = getBankingFieldText(rawCountry).trim();
  const country = BANK_COUNTRY_OPTIONS.find(
    (option) =>
      option.value.toLowerCase() === countryText.toLowerCase() ||
      option.label.toLowerCase() === countryText.toLowerCase(),
  );
  const rawCurrency =
    details.currency || details.currencyCode || details.bankCurrency || "";
  const currencyText = (
    typeof rawCurrency === "object" && rawCurrency !== null
      ? rawCurrency.currencyCode ||
        rawCurrency.code ||
        rawCurrency.value ||
        rawCurrency.symbol ||
        rawCurrency.name ||
        ""
      : rawCurrency
  )
    .toString()
    .trim();
  const currency = CURRENCY_DROPDOWN_OPTIONS.find(
    (option) =>
      option.value.toLowerCase() === currencyText.toLowerCase() ||
      option.symbol.toLowerCase() === currencyText.toLowerCase(),
  );

  return {
    bankName: getBankingFieldText(details.bankName || details.name),
    bankCountry: country?.value || countryText,
    accountHolder: getBankingFieldText(
      details.accountHolder || details.accountHolderName || details.accountName,
    ),
    accountNumber: getBankingFieldText(
      details.accountNumber ||
        details.accountNumberIban ||
        details.accountNumberOrIban ||
        details.iban,
    ),
    swiftBic: getBankingFieldText(
      details.swiftBic || details.swift || details.bic,
    ),
    branch: getBankingFieldText(details.branch || details.branchName),
    currency: currency?.value || currencyText,
    paymentInstructions: getBankingFieldText(
      details.paymentInstructions || details.paymentInstruction,
    ),
  };
};
const getBankingRecordFromResponse = (response) => {
  const body = response?.data || {};
  const candidates = [
    body?.data?.bankingDetails,
    body?.data?.bankingDetail,
    body?.data?.bankDetails,
    body?.bankingDetails,
    body?.bankingDetail,
    body?.bankDetails,
    body?.data?.data?.bankingDetails,
    body?.data?.data?.bankingDetail,
    body?.data?.data,
    body?.data,
    body,
  ];
  const bankingKeys = [
    "bankName",
    "bankCountry",
    "accountHolder",
    "accountNumberOrIban",
    "accountNumber",
    "swiftBic",
    "branch",
    "currency",
    "paymentInstructions",
  ];
  return candidates.find(
    (candidate) =>
      candidate &&
      typeof candidate === "object" &&
      !Array.isArray(candidate) &&
      bankingKeys.some((key) => Object.prototype.hasOwnProperty.call(candidate, key)),
  ) || null;
};
const getBankingResponseMessage = (response) =>
  response?.data?.message ||
  response?.data?.data?.message ||
  response?.response?.data?.message ||
  response?.message ||
  "";
const getBankingCountryLabel = (value) =>
  BANK_COUNTRY_OPTIONS.find((option) => option.value === value)?.label || value;
const getBankingCurrencyLabel = (value) =>
  CURRENCY_DROPDOWN_OPTIONS.find((option) => option.value === value)
    ?.selectedLabel || value;

const DEFAULT_PROFILE = {
  vendorId: "",
  firstName: "Olivia rhye",
  email: "Oliviarhye@example.com",
  phone: "98765 43210",

  biography:
    "Hi there! 👋 I'm an AI enthusiast and fitness aficionado. When I'm not crunching numbers or optimizing algorithms, you can find me hitting the gym.",
  companyName: "",
  countryCode: "+244",
  phoneCountryIso: "AO",
  dob: "",
  approvalStatus: "",
  approvedBy: "",
  approvedAt: "",
  isActive: false,
  lastLoginAt: "",
  accountCreatedAt: "",
  sectors: [],
  products: [],
  documentId: null,
  profileImageUrl: "",
  profileImageId: "",
};

const getId = (value) =>
  typeof value === "string" || typeof value === "number"
    ? String(value)
    : value?._id || value?.id || "";

const getLocalPhoneNumber = (value, dialCode) => {
  const phone = String(value || "").replace(/\s/g, "");
  const code = String(dialCode || "").replace(/\s/g, "");
  return code && phone.startsWith(code) ? phone.slice(code.length) : phone;
};
const getProfileImageId = (vendor) => {
  const imageCandidates = [
    vendor?.profileImage,
    vendor?.profileImageId,
    vendor?.profilePicture,
    vendor?.profilePictureId,
    vendor?.image,
    vendor?.imageId,
    vendor?.avatar,
    vendor?.avatarId,
    vendor?.profile?.image,
    vendor?.profile?.imageId,
  ];

  return imageCandidates.map(getId).find(Boolean) || "";
};

const mapVendorProfile = (vendor) => {
  const profileImageId = getProfileImageId(vendor);

  return {
    ...DEFAULT_PROFILE,
    vendorId: vendor?.vendorId || vendor?.id || vendor?._id || "",
    firstName: vendor?.name || "",
    lastName: vendor?.lastName || "",
    email: vendor?.username || "",
    phone: getLocalPhoneNumber(
      vendor?.phoneNumber || vendor?.phone || "",
      vendor?.countryCode || "",
    ),
    countryCode: vendor?.countryCode || "",
    phoneCountryIso:
      vendor?.phoneCountryIso ||
      vendor?.countryIso ||
      vendor?.country?.code ||
      "",
    companyName: vendor?.companyName || "",
    dob: vendor?.dob || "",
    approvalStatus: vendor?.approvalStatus || "",
    approvedBy: vendor?.approvedBy || "",
    approvedAt: vendor?.approvedAt || "",
    isActive: Boolean(vendor?.isActive),
    lastLoginAt: vendor?.lastLoginAt || "",
    accountCreatedAt: vendor?.accountCreatedAt || vendor?.createdAt || "",
    sectors: Array.isArray(vendor?.sectors) ? vendor.sectors : [],
    products: Array.isArray(vendor?.products) ? vendor.products : [],
    documentId: vendor?.documentId || null,
    profileImageId,
    profileImageUrl: profileImageId
      ? `${backendUrl}/uploads/${encodeURIComponent(profileImageId)}`
      : "",
    biography: vendor?.biography || "",
  };
};

const formatProfileDateForApi = (value) => {
  if (!value) return "";
  const datePart = String(value).match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  if (datePart) return datePart;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};
const formatProfileItems = (items) =>
  Array.isArray(items) && items.length
    ? items
        .map(
          (item) => item?.name || item?.title || item?.id || item?._id || item,
        )
        .join(", ")
    : "—";
const formatCompanyProfileValue = (value) => {
  if (value === null || value === undefined || value === "")
    return "Not provided";
  if (typeof value === "object") {
    return (
      value.name || value.label || value.value || value.title || "Not provided"
    );
  }
  return String(value);
};

const formatDocumentLabel = (value, index) =>
  String(value || `Document ${index + 1}`)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const normalizeCompanyDocument = (document, label, index) => {
  const isUnavailable =
    document === null || document === undefined || document === "";
  const originalName =
    typeof document === "object" && document ? document.originalName || "" : "";
  const documentLabel =
    typeof document === "object" && document
      ? document.label ||
        document.documentLabel ||
        document.documentType ||
        document.type ||
        document.title ||
        label
      : label;

  return {
    key: `${label || "document"}-${index}`,
    label: formatDocumentLabel(documentLabel, index),
    originalName,
    id: isUnavailable ? "" : getId(document),
    unavailable: isUnavailable,
  };
};

const mapCompanyDocuments = (documents) => {
  if (Array.isArray(documents)) {
    return documents.map((document, index) =>
      normalizeCompanyDocument(
        document,
        document?.label || document?.type,
        index,
      ),
    );
  }

  if (!documents || typeof documents !== "object") return [];

  if (documents._id || documents.id || documents.originalName) {
    return [
      normalizeCompanyDocument(documents, documents.label || documents.type, 0),
    ];
  }

  return Object.entries(documents).map(([label, document], index) =>
    normalizeCompanyDocument(
      document,
      document?.label || document?.type || label,
      index,
    ),
  );
};
export default function Setting() {
  const { t, text } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const vendorProfile = useSelector((state) => state.auth.authMe);
  const bankingVendorId = getId(vendorProfile?.vendorId) || getId(vendorProfile);
  const companyProfile = vendorProfile?.companyProfile || {};
  const companyDocuments = mapCompanyDocuments(companyProfile.documents);
  const uploadedCompanyDocuments = companyDocuments.filter(
    (document) => document.id,
  );
  const companyProfileFields = [
    [
      "Company Legal Name",
      companyProfile.companyLegalName ||
        companyProfile.legalName ||
        companyProfile.companyName,
    ],
    ["Trading Name", companyProfile.tradingName],
    [
      "Registration Number",
      companyProfile.registrationNumber || companyProfile.registrationNo,
    ],
    ["Country of Registration", companyProfile.countryOfRegistration],
    [
      "Company Type",
      companyProfile.companyType || companyProfile.companyTypeName,
    ],
  ];
  const [profileLoading, setProfileLoading] = useState(false);
  const [hasCachedProfile] = useState(() => {
    try {
      return Boolean(localStorage.getItem(PROFILE_STORAGE_KEY));
    } catch {
      return false;
    }
  });
  const hasProfileData = Boolean(
    hasCachedProfile ||
    vendorProfile?.id ||
    vendorProfile?._id ||
    vendorProfile?.username ||
    vendorProfile?.companyProfile,
  );
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const fileInputRef = useRef(null);
  const countryDropdownRef = useRef(null);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [activeTab, setActiveTab] = useState(
    ["settings", "documents", "banking"].includes(requestedTab)
      ? requestedTab
      : "profile",
  );
  const [showLogoutModal, setShowLogoutModal] = useState(
    requestedTab === "logout",
  );
  const [profile, setProfile] = useState(() => {
    try {
      const savedProfile = localStorage.getItem(PROFILE_STORAGE_KEY);

      if (savedProfile) {
        return {
          ...DEFAULT_PROFILE,
          ...JSON.parse(savedProfile),
        };
      }
    } catch (error) {
      console.error("Unable to load profile:", error);
    }

    return DEFAULT_PROFILE;
  });

  const [savedProfile, setSavedProfile] = useState(() => {
    try {
      const storedProfile = localStorage.getItem(PROFILE_STORAGE_KEY);

      if (storedProfile) {
        return {
          ...DEFAULT_PROFILE,
          ...JSON.parse(storedProfile),
        };
      }
    } catch (error) {
      console.error("Unable to load saved profile:", error);
    }

    return DEFAULT_PROFILE;
  });

  const [bankingDetails, setBankingDetails] = useState(EMPTY_BANKING_DETAILS);
  const [savedBankingDetails, setSavedBankingDetails] =
    useState(EMPTY_BANKING_DETAILS);
  const [isEditingBanking, setIsEditingBanking] = useState(false);
  const [isSavingBanking, setIsSavingBanking] = useState(false);
  const [isLoadingBanking, setIsLoadingBanking] = useState(false);
  const [isDeletingBanking, setIsDeletingBanking] = useState(false);
  const [bankingDetailsExists, setBankingDetailsExists] = useState(false);
  const [showDeleteBankingConfirmation, setShowDeleteBankingConfirmation] = useState(false);

  const bankingCountryOptions = useMemo(() => {
    const selected = bankingDetails.bankCountry;
    if (
      !selected ||
      BANK_COUNTRY_OPTIONS.some((option) => option.value === selected)
    ) {
      return BANK_COUNTRY_OPTIONS;
    }
    return [
      {
        value: selected,
        label: selected,
        selectedLabel: selected,
        searchText: selected,
      },
      ...BANK_COUNTRY_OPTIONS,
    ];
  }, [bankingDetails.bankCountry]);
  const bankingCurrencyOptions = useMemo(() => {
    const selected = bankingDetails.currency;
    if (
      !selected ||
      CURRENCY_DROPDOWN_OPTIONS.some((option) => option.value === selected)
    ) {
      return CURRENCY_DROPDOWN_OPTIONS;
    }
    return [
      {
        value: selected,
        label: selected,
        selectedLabel: selected,
        searchText: selected,
      },
      ...CURRENCY_DROPDOWN_OPTIONS,
    ];
  }, [bankingDetails.currency]);

  const selectedCountry =
    countryCodes.find((country) => country.code === profile.phoneCountryIso) ||
    countryCodes.find((country) => country.dial_code === profile.countryCode) ||
    countryCodes.find((country) => country.code === "AO") ||
    countryCodes[0];
  const filteredCountryCodes = useMemo(() => {
    const search = countrySearch.trim().toLowerCase();
    if (!search) return countryCodes;
    return countryCodes.filter((country) =>
      [country.name, country.code, country.dial_code]
        .join(" ")
        .toLowerCase()
        .includes(search),
    );
  }, [countrySearch]);
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  const [profileMessage, setProfileMessage] = useState("");
  const [profileImage, setProfileImage] = useState(() => {
    return localStorage.getItem(PROFILE_IMAGE_STORAGE_KEY) || "";
  });

  const [savedProfileImage, setSavedProfileImage] = useState(() => {
    return localStorage.getItem(PROFILE_IMAGE_STORAGE_KEY) || "";
  });

  const [profileImageError, setProfileImageError] = useState("");
  const [profileImageId, setProfileImageId] = useState("");
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [settings, setSettings] = useState({
    emailNotifications: true,
    contractNotifications: true,
    inquiryNotifications: true,
    paymentNotifications: false,
    loginAlerts: true,
    weeklyReports: false,
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [passwordError, setPasswordError] = useState("");
  const [isPasswordSectionOpen, setIsPasswordSectionOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;
    setProfileLoading(true);
    Promise.resolve(dispatch(getVendorProfileAction())).then(
      () => {
        if (isMounted) setProfileLoading(false);
      },
      () => {
        if (isMounted) setProfileLoading(false);
      },
    );
    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  useEffect(() => {
    if (!countryDropdownOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!countryDropdownRef.current?.contains(event.target)) {
        setCountryDropdownOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setCountryDropdownOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [countryDropdownOpen]);

  useEffect(() => {
    if (
      !vendorProfile ||
      (!vendorProfile.id &&
        !vendorProfile._id &&
        !vendorProfile.username &&
        !vendorProfile.companyProfile)
    ) {
      return;
    }

    const mappedProfile = mapVendorProfile(vendorProfile);
    setProfile(mappedProfile);
    setSavedProfile(mappedProfile);
    setProfileImage(mappedProfile.profileImageUrl);
    setSavedProfileImage(mappedProfile.profileImageUrl);
    setProfileImageId(mappedProfile.profileImageId);
    setProfileImageFile(null);
  }, [vendorProfile]);
  useEffect(() => {
    if (activeTab !== "banking" || !bankingVendorId) return undefined;

    let isMounted = true;
    setIsLoadingBanking(true);

    const loadBankingDetails = async () => {
      try {
        const response = await bankingDetailsService.getBankingDetails();
        if (!isMounted) return;
        const status = response?.status ?? response?.response?.status;
        if (status >= 200 && status < 300) {
          const record = getBankingRecordFromResponse(response);
          const mappedDetails = record
            ? mapBankingDetails(record)
            : { ...EMPTY_BANKING_DETAILS };
          setBankingDetails(mappedDetails);
          setSavedBankingDetails(mappedDetails);
          setBankingDetailsExists(Boolean(record));
          setIsEditingBanking(false);
          setShowDeleteBankingConfirmation(false);
        } else if (status === 404) {
          setBankingDetails({ ...EMPTY_BANKING_DETAILS });
          setSavedBankingDetails({ ...EMPTY_BANKING_DETAILS });
          setBankingDetailsExists(false);
          setIsEditingBanking(false);
        } else {
          toogleErrorMessage(
            getBankingResponseMessage(response) || "Unable to load banking details.",
          );
        }
      } catch (error) {
        if (isMounted) {
          toogleErrorMessage(
            getBankingResponseMessage(error) || error?.message || "Unable to load banking details.",
          );
        }
      } finally {
        if (isMounted) setIsLoadingBanking(false);
      }
    };

    loadBankingDetails();
    return () => {
      isMounted = false;
    };
  }, [activeTab, bankingVendorId]);

  useEffect(() => {
    if (["profile", "settings", "documents", "banking"].includes(requestedTab)) {
      setActiveTab(requestedTab);

      setShowLogoutModal(false);
    }

    if (requestedTab === "logout") {
      setShowLogoutModal(true);
    }
  }, [requestedTab]);
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setShowLogoutModal(false);
      }
    };

    if (showLogoutModal) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showLogoutModal]);

  const isBankingDetailsChanged = Object.keys(EMPTY_BANKING_DETAILS).some(
    (key) => bankingDetails[key] !== savedBankingDetails[key],
  );

  const isProfileTextChanged = Object.keys(profile).some(
    (key) => profile[key] !== savedProfile[key],
  );

  const isProfileImageChanged = profileImage !== savedProfileImage;

  const isProfileChanged = isProfileTextChanged || isProfileImageChanged;

  const initials = `${profile.firstName?.charAt(0) || ""}${
    profile.lastName?.charAt(0) || ""
  }`.toUpperCase();

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    const profileValue =
      name === "phone" &&
      profile.countryCode &&
      value.startsWith(`${profile.countryCode} `)
        ? sanitizeNumericInput(
            value.slice(profile.countryCode.length).trim(),
            { allowDecimal: false },
          )
        : name === "phone"
          ? sanitizeNumericInput(value, { allowDecimal: false })
          : value;

    setProfile((previous) => ({
      ...previous,

      [name]: profileValue,
    }));

    setProfileMessage("");
  };

  const handleCountrySelect = (country) => {
    setProfile((previous) => ({
      ...previous,
      countryCode: country.dial_code,
      phoneCountryIso: country.code,
    }));
    setCountryDropdownOpen(false);
    setCountrySearch("");
    setProfileMessage("");
  };

  const handleEditProfile = () => {
    setIsEditingProfile(true);

    setProfileMessage("");
  };

  const handleCancelProfileEdit = () => {
    setProfile({
      ...savedProfile,
    });

    setProfileImage(savedProfileImage);
    setProfileImageId(getProfileImageId(savedProfile));
    setProfileImageFile(null);

    setProfileImageError("");

    setProfileMessage("");

    setIsEditingProfile(false);
  };

  const handleBankingDetailsChange = (event) => {
    const { name, value } = event.target;
    setBankingDetails((current) => ({ ...current, [name]: value }));
  };

  const handleBankingSelect = (name, value) => {
    setBankingDetails((current) => ({ ...current, [name]: value }));
  };

  const handleEditBanking = () => {
    setIsEditingBanking(true);
  };

  const handleCancelBankingEdit = () => {
    setBankingDetails({ ...savedBankingDetails });
    setIsEditingBanking(false);
    setShowDeleteBankingConfirmation(false);
  };

  const handleBankingSubmit = async (event) => {
    event.preventDefault();
    if (!isBankingDetailsChanged || isSavingBanking) return;
    if (bankingDetailsExists && !bankingVendorId) {
      toogleErrorMessage("Unable to identify the vendor banking account.");
      return;
    }

    setIsSavingBanking(true);
    const payload = {
      bankName: bankingDetails.bankName.trim(),
      bankCountry: getBankingCountryLabel(bankingDetails.bankCountry),
      accountHolder: bankingDetails.accountHolder.trim(),
      accountNumberOrIban: bankingDetails.accountNumber.trim(),
      swiftBic: bankingDetails.swiftBic.trim(),
      branch: bankingDetails.branch.trim(),
      currency: bankingDetails.currency,
      paymentInstructions: bankingDetails.paymentInstructions.trim(),
    };

    try {
      const response = bankingDetailsExists
        ? await bankingDetailsService.updateBankingDetails(payload)
        : await bankingDetailsService.createBankingDetails(payload);
      const status = response?.status ?? response?.response?.status;
      if (status >= 200 && status < 300) {
        const record = getBankingRecordFromResponse(response);
        const updatedBankingDetails = record
          ? mapBankingDetails(record)
          : { ...bankingDetails };
        setBankingDetails(updatedBankingDetails);
        setSavedBankingDetails(updatedBankingDetails);
        setBankingDetailsExists(true);
        toogleSuccessMessage(
          getBankingResponseMessage(response) ||
            (bankingDetailsExists
              ? "Banking details updated successfully."
              : "Banking details created successfully."),
        );
        setIsEditingBanking(false);
        setShowDeleteBankingConfirmation(false);
      } else {
        toogleErrorMessage(
          getBankingResponseMessage(response) || "Unable to save banking details.",
        );
      }
    } catch (error) {
      toogleErrorMessage(
        getBankingResponseMessage(error) ||
          error?.message ||
          "Unable to save banking details.",
      );
    } finally {
      setIsSavingBanking(false);
    }
  };

  const handleDeleteBanking = async () => {
    if (!bankingVendorId || !bankingDetailsExists || isDeletingBanking) return;
    setIsDeletingBanking(true);
    try {
      const response = await bankingDetailsService.deleteBankingDetails(bankingVendorId);
      const status = response?.status ?? response?.response?.status;
      if ((status >= 200 && status < 300) || status === 404) {
        const emptyDetails = { ...EMPTY_BANKING_DETAILS };
        setBankingDetails(emptyDetails);
        setSavedBankingDetails(emptyDetails);
        setBankingDetailsExists(false);
        setIsEditingBanking(false);
        setShowDeleteBankingConfirmation(false);
        toogleSuccessMessage(
          getBankingResponseMessage(response) || "Banking details deleted.",
        );
      } else {
        toogleErrorMessage(
          getBankingResponseMessage(response) || "Unable to delete banking details.",
        );
      }
    } catch (error) {
      toogleErrorMessage(
        getBankingResponseMessage(error) ||
          error?.message ||
          "Unable to delete banking details.",
      );
    } finally {
      setIsDeletingBanking(false);
    }
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    if (!isProfileChanged || isSavingProfile) return;

    setIsSavingProfile(true);
    try {
      let uploadedImageId = profileImageId;
      if (profileImageFile) {
        const uploadResult = await dispatch(
          uploadVendorProfileImageAction(profileImageFile),
        );
        if (!uploadResult?.success) return;
        uploadedImageId = uploadResult.uploadId;
        setProfileImageId(uploadedImageId);
        setProfileImageFile(null);
      } else if (!profileImage) {
        uploadedImageId = "";
      }

      const countryCode = selectedCountry.dial_code;
      const phoneNumber = getLocalPhoneNumber(profile.phone, countryCode);

      const payload = {
        name: [profile.firstName, profile.lastName]
          .filter(Boolean)
          .join(" ")
          .trim(),
        dob: formatProfileDateForApi(profile.dob),
        phoneNumber,
        countryCode,
        companyName: profile.companyName || "",
        biography: profile.biography || "",
        documentId: getId(profile.documentId) || null,
        sectors: Array.isArray(profile.sectors)
          ? profile.sectors.map(getId).filter(Boolean)
          : [],
        products: Array.isArray(profile.products)
          ? profile.products.map(getId).filter(Boolean)
          : [],
        profileImage: uploadedImageId || null,
      };

      const response = await dispatch(updateVendorProfileAction(payload));
      if (response?.status >= 200 && response?.status < 300) {
        const imageUrl = uploadedImageId
          ? backendUrl + "/uploads/" + encodeURIComponent(uploadedImageId)
          : "";
        const updatedProfile = {
          ...profile,
          profileImageId: uploadedImageId,
          profileImageUrl: imageUrl,
        };

        setProfile(updatedProfile);
        setSavedProfile(updatedProfile);
        setProfileImage(imageUrl);
        setSavedProfileImage(imageUrl);
        setProfileImageId(uploadedImageId);
        setProfileImageFile(null);
        setProfileMessage("");
        setProfileImageError("");
        setIsEditingProfile(false);
      }
    } catch (error) {
      toogleErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to update vendor profile.",
      );
    } finally {
      setIsSavingProfile(false);
    }
  };

  const openProfileImagePicker = () => {
    setProfileImageError("");

    fileInputRef.current?.click();
  };

  const handleProfileImageChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setProfileImageError("");
    if (!file.type.startsWith("image/")) {
      setProfileImageError(t("validation.imageInvalid"));
      toogleErrorMessage("Please choose a valid image file.");

      event.target.value = "";

      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setProfileImageError(t("validation.imageTooLarge"));
      toogleErrorMessage("Profile image must be smaller than 2 MB.");

      event.target.value = "";

      return;
    }

    setProfileImageFile(file);

    const reader = new FileReader();

    reader.onload = () => {
      const imageData = String(reader.result || "");

      setProfileImage(imageData);

      setIsEditingProfile(true);

      setProfileMessage("");
    };

    reader.onerror = () => {
      setProfileImageFile(null);
      setProfileImageError(
        "Unable to read this image. Please try another image.",
      );
      toogleErrorMessage(
        "Unable to read this image. Please try another image.",
      );
    };

    reader.readAsDataURL(file);

    event.target.value = "";
  };
  const handleSettingChange = (name) => {
    setSettings((previous) => ({
      ...previous,

      [name]: !previous[name],
    }));
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;

    setPasswordForm((previous) => ({
      ...previous,

      [name]: value,
    }));

    setPasswordError("");
  };

  const canSubmitPassword =
    passwordForm.currentPassword.length > 0 &&
    passwordForm.newPassword.length >= 6 &&
    passwordForm.confirmPassword.length > 0 &&
    passwordForm.newPassword === passwordForm.confirmPassword;

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();

    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(t("validation.passwordFieldsRequired"));

      return;
    }

    if (newPassword.length < 6) {
      setPasswordError(t("validation.passwordTooShort"));

      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t("validation.passwordMismatchSettings"));

      return;
    }

    setPasswordError("");

    const response = await dispatch(vendorChangePasswordAction(passwordForm));

    if (response?.status < 200 || response?.status >= 300) {
      return;
    }
    setPasswordForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setIsPasswordSectionOpen(false);
  };

  const handleLogout = () => {
    dispatch(clearAuthSessionAction());

    setShowLogoutModal(false);

    navigate("/");
  };

  return (
    <div className="dashboard-page settings-page">
      <div className="settings-shell">
        <header className="settings-header">
          <h1>{t("ui.settings")}</h1>

          <p>{t("ui.manage.your.profile.preferences.and.account.security")}</p>
        </header>
        <nav className="settings-tabs" aria-label={t("ui.settings.navigation")}>
          <button
            type="button"
            className={
              activeTab === "profile" ? "settings-tab active" : "settings-tab"
            }
            onClick={() => setActiveTab("profile")}
          >
            {t("ui.my.profile")}
          </button>
          <button
            type="button"
            className={
              activeTab === "documents" ? "settings-tab active" : "settings-tab"
            }
            onClick={() => setActiveTab("documents")}
          >
            {t("ui.documents")}
          </button>

          <button
            type="button"
            className={
              activeTab === "banking" ? "settings-tab active" : "settings-tab"
            }
            onClick={() => setActiveTab("banking")}
          >
            {text("Banking Details")}
          </button>
          <button
            type="button"
            className={activeTab === "settings" ? "settings-tab active" : "settings-tab"
            }
            onClick={() => setActiveTab("settings")}
          >
            {t("ui.settings.1qomqn7")}
          </button>

          {/* <button
            type="button"
            className="settings-tab"
            onClick={() =>
              setShowLogoutModal(true)
            }
          >
            Logout
          </button> */}
        </nav>
        <main className="settings-main">
          {activeTab === "profile" && (
            <section className="profile-panel">
              {profileLoading && !hasProfileData ? (
                <LoadingSpinner
                  className="settings-profile-loading-state"
                  label={t("ui.loadingProfile")}
                />
              ) : (
                <form onSubmit={handleProfileSubmit}>
                  {/* Profile Heading */}

                  <div className="profile-panel-header">
                    <div>
                      <h2>{t("ui.your.profile")}</h2>

                      <p>{t("ui.please.update.your.profile.settings.here")}</p>
                    </div>

                    <div className="profile-header-actions">
                      {isEditingProfile && (
                        <button
                          type="button"
                          className="secondary-action-btn"
                          onClick={handleCancelProfileEdit}
                        >
                          {t("ui.cancel")}
                        </button>
                      )}

                      {!isEditingProfile ? (
                        <button
                          type="button"
                          className="dark-action-btn"
                          onClick={handleEditProfile}
                        >
                          {t("ui.edit.and.update")}
                        </button>
                      ) : (
                        <button
                          type="submit"
                          className="dark-action-btn"
                          disabled={!isProfileChanged || isSavingProfile}
                        >
                          {isSavingProfile
                            ? text("Saving...")
                            : text("Update Profile")}
                        </button>
                      )}
                    </div>
                  </div>

                  <ProfileRow label={t("ui.name")}>
                    {isEditingProfile ? (
                      <input
                        type="text"
                        name="firstName"
                        value={profile.firstName}
                        onChange={handleProfileChange}
                        placeholder={t("ui.first.name")}
                        className="profile-row-input"
                      />
                    ) : (
                      // <div className="profile-name-grid">

                      //   <input
                      //     type="text"
                      //     name="lastName"
                      //     value={
                      //       profile.lastName
                      //     }
                      //     onChange={
                      //       handleProfileChange
                      //     }
                      //     placeholder="Last name"
                      //     className="profile-row-input"
                      //   />

                      // </div>
                      <span className="profile-value">
                        {profile.firstName}
                        {profile.lastName ? ` ${profile.lastName}` : ""}
                      </span>
                    )}
                  </ProfileRow>

                  <ProfileRow label={t("ui.email")}>
                    {isEditingProfile ? (
                      <input
                        type="email"
                        name="email"
                        value={profile.email}
                        onChange={handleProfileChange}
                        className="profile-row-input"
                      />
                    ) : (
                      <span className="profile-value">{profile.email}</span>
                    )}
                  </ProfileRow>

                  <ProfileRow label={t("ui.phoneNumber")}>
                    {isEditingProfile ? (
                      <div className="profile-phone-field">
                        <div
                          className="profile-country-picker"
                          ref={countryDropdownRef}
                        >
                          <button
                            type="button"
                            className="profile-country-trigger"
                            aria-label={t("ui.select.country.calling.code")}
                            aria-haspopup="listbox"
                            aria-expanded={countryDropdownOpen}
                            onClick={() => {
                              setCountryDropdownOpen((open) => !open);
                              setCountrySearch("");
                            }}
                          >
                            <img
                              src={
                                "https://flagcdn.com/24x18/" +
                                selectedCountry.code.toLowerCase() +
                                ".png"
                              }
                              alt={selectedCountry.code}
                              width="24"
                              height="18"
                            />
                            <span>{selectedCountry.dial_code}</span>
                            <span
                              className="profile-country-chevron"
                              aria-hidden="true"
                            />
                          </button>

                          {countryDropdownOpen && (
                            <div className="profile-country-menu">
                              <div className="profile-country-search">
                                <input
                                  type="search"
                                  value={countrySearch}
                                  onChange={(event) =>
                                    setCountrySearch(event.target.value)
                                  }
                                  placeholder={t("ui.search.country.or.code")}
                                  aria-label={t("ui.search.countries")}
                                  autoFocus
                                />
                              </div>
                              <div
                                className="profile-country-options"
                                role="listbox"
                                aria-label={t("ui.country.calling.codes")}
                              >
                                {filteredCountryCodes.length > 0 ? (
                                  filteredCountryCodes.map((country) => (
                                    <button
                                      type="button"
                                      role="option"
                                      aria-selected={
                                        country.code === selectedCountry.code
                                      }
                                      className={
                                        "profile-country-option" +
                                        (country.code === selectedCountry.code
                                          ? " selected"
                                          : "")
                                      }
                                      key={country.code + country.dial_code}
                                      onClick={() =>
                                        handleCountrySelect(country)
                                      }
                                    >
                                      <img
                                        src={
                                          "https://flagcdn.com/24x18/" +
                                          country.code.toLowerCase() +
                                          ".png"
                                        }
                                        alt={country.code}
                                        width="24"
                                        height="18"
                                      />
                                      <span className="profile-country-name">
                                        {country.name}
                                      </span>
                                      <span className="profile-country-dial">
                                        {country.dial_code}
                                      </span>
                                    </button>
                                  ))
                                ) : (
                                  <div className="profile-country-empty">
                                    {t("ui.no.countries.found")}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <input
                          type="tel"
                          name="phone"
                          value={profile.phone}
                          onChange={handleProfileChange}
                          placeholder={t("ui.phone.number.1qbu0rf")}
                          onKeyDown={(event) =>
                            handleNumericInputKeyDown(event, { allowDecimal: false })
                          }
                          inputMode="numeric"
                          pattern="[0-9]*"
                          autoComplete="tel-national"
                          className="profile-row-input profile-phone-number-input"
                        />
                      </div>
                    ) : (
                      <div className="profile-phone-readonly">
                        <img
                          src={
                            "https://flagcdn.com/24x18/" +
                            selectedCountry.code.toLowerCase() +
                            ".png"
                          }
                          alt={selectedCountry.code}
                          width="24"
                          height="18"
                        />
                        <span>
                          {selectedCountry.dial_code}{" "}
                          {getLocalPhoneNumber(
                            profile.phone,
                            selectedCountry.dial_code,
                          ) || "\u2014"}
                        </span>
                      </div>
                    )}
                  </ProfileRow>

                  <ProfileRow label={t("ui.profilePicture")}>
                    <div className="profile-picture-content">
                      <button
                        type="button"
                        className="profile-avatar-button"
                        onClick={openProfileImagePicker}
                        aria-label={t("ui.change.profile.image")}
                      >
                        <span className="profile-avatar">
                          {profileImage ? (
                            <img src={profileImage} alt={t("ui.profile")} />
                          ) : (
                            <span>{initials || "AU"}</span>
                          )}
                        </span>
                      </button>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        className="profile-file-input"
                        onChange={handleProfileImageChange}
                      />

                      <div className="profile-picture-actions">
                        <button
                          type="button"
                          className="profile-image-change-btn"
                          onClick={openProfileImagePicker}
                        >
                          {profileImage
                            ? t("ui.changeAction")
                            : t("ui.uploadAction")}
                        </button>
                      </div>
                    </div>

                    {profileImageError && (
                      <div className="profile-image-error">
                        {text(profileImageError)}
                      </div>
                    )}
                  </ProfileRow>
                  <ProfileRow
                    label={
                      <span className="biography-title">
                        {t("ui.biography")}
                        {/* <span 
                        className="biography-info"
                        title="About you"
                      >
                        i
                      </span> */}
                      </span>
                    }
                    alignTop
                  >
                    <div className="biography-wrapper">
                      <textarea
                        name="biography"
                        value={profile.biography}
                        onChange={handleProfileChange}
                        readOnly={!isEditingProfile}
                        maxLength={325}
                        className="biography-input"
                        placeholder={t("ui.tell.us.about.yourself")}
                      />

                      <span className="biography-count">
                        {325 - (profile.biography?.length || 0)}{" "}
                        {t("ui.characters.remaining")}
                      </span>
                    </div>
                  </ProfileRow>

                  {companyProfileFields.map(([label, value]) => (
                    <ProfileRow label={label} key={label}>
                      <input
                        type="text"
                        value={formatCompanyProfileValue(value)}
                        readOnly
                        disabled
                        aria-label={text(label)}
                        className="profile-row-input company-profile-readonly-input"
                      />
                    </ProfileRow>
                  ))}
                  {/* Profile Success */}

                  {profileMessage && (
                    <div className="profile-success-message">
                      {text(profileMessage)}
                    </div>
                  )}
                </form>
              )}
            </section>
          )}

          {/* =====================
              SETTINGS
          ====================== */}

          {activeTab === "documents" && (
            <section className="settings-section company-documents-panel">
              <div className="section-heading">
                <div>
                  <h2>{text("Documents")}</h2>
                  <p>{text("Company documents from your vendor profile.")}</p>
                </div>
              </div>
              <div className="company-documents-list">
                {uploadedCompanyDocuments.length ? (
                  uploadedCompanyDocuments.map((document) => (
                    <article
                      className="company-document-row"
                      key={document.key}
                    >
                      <div className="company-document-details">
                        <strong className="company-document-label">
                          {text(document.label)}
                        </strong>
                        <span className="company-document-filename">
                          {document.originalName ||
                            (!document.unavailable &&
                              text("Filename unavailable"))}
                        </span>
                      </div>
                      {document.id ? (
                        <button
                          type="button"
                          className="dark-action-btn company-document-view-button"
                          onClick={() =>
                            window.open(
                              `${backendUrl}/uploads/${encodeURIComponent(document.id)}`,
                              "_blank",
                              "noopener,noreferrer",
                            )
                          }
                        >
                          {text("View")}
                        </button>
                      ) : (
                        <span className="company-document-unavailable">
                          {text(
                            document.unavailable
                              ? "Not uploaded"
                              : "View unavailable",
                          )}
                        </span>
                      )}
                    </article>
                  ))
                ) : (
                  <p className="company-documents-empty">
                    {text("No documents uploaded.")}
                  </p>
                )}
              </div>
            </section>
          )}
          {activeTab === "banking" &&
            (profileLoading || isLoadingBanking ? (
              <section className="profile-panel">
                <LoadingSpinner
                  className="settings-profile-loading-state"
                  label={t("ui.loadingProfile")}
                />
              </section>
            ) : (
              <section className="profile-panel banking-details-panel">
                <form onSubmit={handleBankingSubmit}>
                  <div className="profile-panel-header">
                    <div>
                      <h2>{sentenceCaseLabel(text("Banking Details"))}</h2>
                      <p>
                        {text(
                          "View and manage the bank account used for payments.",
                        )}
                      </p>
                    </div>
                    <div className="profile-header-actions">
                      {isEditingBanking && bankingDetailsExists && (
                        <button
                          type="button"
                          className="secondary-action-btn banking-delete-btn"
                          onClick={() => setShowDeleteBankingConfirmation(true)}
                          disabled={isSavingBanking || isDeletingBanking}
                        >
                          {text("Delete")}
                        </button>
                      )}
                      {isEditingBanking && (
                        <button
                          type="button"
                          className="secondary-action-btn"
                          onClick={handleCancelBankingEdit}
                          disabled={isSavingBanking || isDeletingBanking}
                        >
                          {t("ui.cancel")}
                        </button>
                      )}
                      {!isEditingBanking ? (
                        <button
                          type="button"
                          className="dark-action-btn"
                          onClick={handleEditBanking}
                        >
                          {bankingDetailsExists ? t("ui.edit") : text("Add bank details")}
                        </button>
                      ) : (
                        <button
                          type="submit"
                          className="dark-action-btn"
                          disabled={!isBankingDetailsChanged || isSavingBanking}
                        >
                          {isSavingBanking
                            ? text("Saving...")
                            : text(bankingDetailsExists ? "Save changes" : "Create banking details")}
                        </button>
                      )}
                    </div>
                  </div>

                  {showDeleteBankingConfirmation && (
                    <div className="banking-delete-confirmation" role="alert">
                      <span>{text("Delete the saved banking details?")}</span>
                      <div>
                        <button
                          type="button"
                          className="secondary-action-btn"
                          onClick={() => setShowDeleteBankingConfirmation(false)}
                          disabled={isDeletingBanking}
                        >
                          {t("ui.cancel")}
                        </button>
                        <button
                          type="button"
                          className="banking-delete-confirm-btn"
                          onClick={handleDeleteBanking}
                          disabled={isDeletingBanking}
                        >
                          {isDeletingBanking ? text("Deleting...") : text("Delete banking details")}
                        </button>
                      </div>
                    </div>
                  )}

                  <ProfileRow label={text("Bank name")}>
                    {isEditingBanking ? (
                      <input
                        type="text"
                        name="bankName"
                        value={bankingDetails.bankName}
                        onChange={handleBankingDetailsChange}
                        placeholder={text("Enter bank name")}
                        autoComplete="off"
                        disabled={isSavingBanking || isDeletingBanking}
                        className="profile-row-input"
                      />
                    ) : (
                      <span className="profile-value">
                        {bankingDetails.bankName || text("Not provided")}
                      </span>
                    )}
                  </ProfileRow>

                  <ProfileRow label={text("Bank country")}>
                    {isEditingBanking ? (
                      <CustomDropdown
                        className="banking-details-dropdown"
                        ariaLabel={text("Bank country")}
                        value={bankingDetails.bankCountry}
                        options={bankingCountryOptions}
                        placeholder={text("Select bank country")}
                        searchable
                        disabled={isSavingBanking || isDeletingBanking}
                        searchPlaceholder={text("Search bank countries...")}
                        emptyMessage={text("No countries found")}
                        onChange={(value) =>
                          handleBankingSelect("bankCountry", value)
                        }
                      />
                    ) : (
                      <span className="profile-value">
                        {getBankingCountryLabel(bankingDetails.bankCountry) ||
                          text("Not provided")}
                      </span>
                    )}
                  </ProfileRow>

                  <ProfileRow label={text("Account holder")}>
                    {isEditingBanking ? (
                      <input
                        type="text"
                        name="accountHolder"
                        value={bankingDetails.accountHolder}
                        onChange={handleBankingDetailsChange}
                        placeholder={text("Enter account holder name")}
                        autoComplete="name"
                        disabled={isSavingBanking || isDeletingBanking}
                        className="profile-row-input"
                      />
                    ) : (
                      <span className="profile-value">
                        {bankingDetails.accountHolder || text("Not provided")}
                      </span>
                    )}
                  </ProfileRow>

                  <ProfileRow label={text("Account number / IBAN")}>
                    {isEditingBanking ? (
                      <input
                        type="text"
                        name="accountNumber"
                        value={bankingDetails.accountNumber}
                        onChange={handleBankingDetailsChange}
                        placeholder={text("Enter account number or IBAN")}
                        autoComplete="off"
                        disabled={isSavingBanking || isDeletingBanking}
                        className="profile-row-input"
                      />
                    ) : (
                      <span className="profile-value">
                        {bankingDetails.accountNumber || text("Not provided")}
                      </span>
                    )}
                  </ProfileRow>

                  <ProfileRow label={text("SWIFT / BIC")}>
                    {isEditingBanking ? (
                      <input
                        type="text"
                        name="swiftBic"
                        value={bankingDetails.swiftBic}
                        onChange={handleBankingDetailsChange}
                        placeholder={text("Enter SWIFT / BIC")}
                        autoComplete="off"
                        disabled={isSavingBanking || isDeletingBanking}
                        className="profile-row-input"
                      />
                    ) : (
                      <span className="profile-value">
                        {bankingDetails.swiftBic || text("Not provided")}
                      </span>
                    )}
                  </ProfileRow>

                  <ProfileRow label={text("Branch")}>
                    {isEditingBanking ? (
                      <input
                        type="text"
                        name="branch"
                        value={bankingDetails.branch}
                        onChange={handleBankingDetailsChange}
                        placeholder={text("Enter branch")}
                        autoComplete="off"
                        disabled={isSavingBanking || isDeletingBanking}
                        className="profile-row-input"
                      />
                    ) : (
                      <span className="profile-value">
                        {bankingDetails.branch || text("Not provided")}
                      </span>
                    )}
                  </ProfileRow>

                  <ProfileRow label={text("Currency")}>
                    {isEditingBanking ? (
                      <CustomDropdown
                        className="banking-details-dropdown"
                        ariaLabel={text("Currency")}
                        value={bankingDetails.currency}
                        options={bankingCurrencyOptions}
                        placeholder={text("Select currency")}
                        searchable
                        disabled={isSavingBanking || isDeletingBanking}
                        searchPlaceholder={text("Search currencies...")}
                        emptyMessage={text("No currencies found")}
                        onChange={(value) =>
                          handleBankingSelect("currency", value)
                        }
                      />
                    ) : (
                      <span className="profile-value">
                        {getBankingCurrencyLabel(bankingDetails.currency) ||
                          text("Not provided")}
                      </span>
                    )}
                  </ProfileRow>

                  <ProfileRow label={text("Payment instructions")} alignTop>
                    {isEditingBanking ? (
                      <textarea
                        name="paymentInstructions"
                        value={bankingDetails.paymentInstructions}
                        onChange={handleBankingDetailsChange}
                        placeholder={text(
                          "Add any payment instructions for your clients.",
                        )}
                        rows={4}
                        disabled={isSavingBanking || isDeletingBanking}
                        className="biography-input banking-details-textarea"
                      />
                    ) : (
                      <span className="profile-value banking-details-instructions">
                        {bankingDetails.paymentInstructions ||
                          text("Not provided")}
                      </span>
                    )}
                  </ProfileRow>
                </form>
              </section>
            ))}
          {activeTab === "settings" && (
            <div className="settings-stack">
              {/* =====================
                  CHANGE PASSWORD
              ====================== */}

              <section className={"settings-section change-password-section" + (isPasswordSectionOpen ? " is-open" : "")}>
                <div className="section-heading change-password-heading">
                  <div>
                    <h2>{t("ui.change.password")}</h2>

                    <p>
                      {t("ui.update.your.password.to.keep.your.account.secure")}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="dark-action-btn change-password-toggle-btn"
                    aria-expanded={isPasswordSectionOpen}
                    aria-controls="changePasswordForm"
                    onClick={() => setIsPasswordSectionOpen((open) => !open)}
                  >
                    {isPasswordSectionOpen ? (
                      t("ui.close")
                    ) : (
                      <>
                        <span>{t("ui.update.password")}</span>
                        <svg className="change-password-chevron" viewBox="0 0 16 16" aria-hidden="true">
                          <path d="m4 6 4 4 4-4" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
                {isPasswordSectionOpen && (
                  <form
                    id="changePasswordForm"
                    className="password-form"
                    onSubmit={handlePasswordSubmit}
                  >
                    <div className="password-grid">
                      <Field
                        label={t("ui.currentPassword")}
                        type="password"
                        name="currentPassword"
                        value={passwordForm.currentPassword}
                        onChange={handlePasswordChange}
                        placeholder={t("ui.enter.current.password")}
                        autoComplete="current-password"
                      />

                      <Field
                        label={t("ui.newPassword")}
                        type="password"
                        name="newPassword"
                        value={passwordForm.newPassword}
                        onChange={handlePasswordChange}
                        placeholder={t("ui.enter.new.password")}
                        autoComplete="new-password"
                      />

                      <Field
                        label={t("ui.confirmPassword")}
                        type="password"
                        name="confirmPassword"
                        value={passwordForm.confirmPassword}
                        onChange={handlePasswordChange}
                        placeholder={t("ui.confirm.new.password")}
                        autoComplete="new-password"
                      />
                    </div>

                    {passwordError && (
                      <div className="form-message error">
                        {text(passwordError)}
                      </div>
                    )}

                    <div className="password-form-actions">
                      <button type="submit" className="dark-action-btn" disabled={!canSubmitPassword}>
                        {text("Save Password")}
                      </button>
                    </div>
                  </form>
                )}
              </section>

              {/* =====================
                  NOTIFICATIONS
              ====================== */}

              <section className="settings-section">
                <div className="section-heading">
                  <div>
                    <h2>{t("ui.notifications")}</h2>

                    <p>{t("ui.choose.the.updates.you.want.to.receive")}</p>
                  </div>
                </div>

                <div className="settings-list">
                  <SettingRow
                    title={t("ui.email.notifications")}
                    description={t("ui.accountUpdatesEmail")}
                    checked={settings.emailNotifications}
                    onChange={() => handleSettingChange("emailNotifications")}
                  />

                  <SettingRow
                    title={t("ui.new.inquiry.notifications")}
                    description={t("ui.newInquiryCreated")}
                    checked={settings.inquiryNotifications}
                    onChange={() => handleSettingChange("inquiryNotifications")}
                  />

                  <SettingRow
                    title={t("ui.contract.notifications")}
                    description={t("ui.contractDecisionUpdates")}
                    checked={settings.contractNotifications}
                    onChange={() =>
                      handleSettingChange("contractNotifications")
                    }
                  />

                  <SettingRow
                    title={t("ui.payment.notifications")}
                    description={t("ui.newPaymentReceived")}
                    checked={settings.paymentNotifications}
                    onChange={() => handleSettingChange("paymentNotifications")}
                  />

                  <SettingRow
                    title={t("ui.login.alerts")}
                    description={t("ui.newDeviceAlert")}
                    checked={settings.loginAlerts}
                    onChange={() => handleSettingChange("loginAlerts")}
                  />

                  <SettingRow
                    title={t("ui.weekly.activity.report")}
                    description={t("ui.adminActivitySummary")}
                    checked={settings.weeklyReports}
                    onChange={() => handleSettingChange("weeklyReports")}
                  />
                </div>
              </section>
            </div>
          )}
        </main>
      </div>

      {/* =====================
          LOGOUT POPUP
      ====================== */}

      {showLogoutModal && (
        <div
          className="logout-modal-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowLogoutModal(false);
            }
          }}
        >
          <div
            className="logout-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="logout-modal-title"
          >
            <div className="logout-modal-icon">
              <img alt="" src="/assets/logout.svg"></img>
            </div>

            <h2 id="logout-modal-title">{t("ui.logout")}</h2>

            <p>
              {t("ui.you.are.signed.in.as")} <strong>{profile.email}</strong>
              {t(
                "ui.you.will.need.to.sign.in.again.to.access.the.admin.portal",
              )}
            </p>

            <div className="logout-modal-actions">
              <button
                type="button"
                className="secondary-action-btn"
                onClick={() => setShowLogoutModal(false)}
              >
                {t("ui.cancel")}
              </button>

              <button
                type="button"
                className="dark-action-btn"
                onClick={handleLogout}
              >
                {t("ui.logout.a799ze")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/*
|--------------------------------------------------------------------------
| Profile Row
|--------------------------------------------------------------------------
*/

function ProfileRow({ label, children, alignTop = false }) {
  const { text } = useTranslation();
  return (
    <div className={`profile-row ${alignTop ? "profile-row-top" : ""}`}>
      <div className="profile-row-label">{text(label)}</div>

      <div className="profile-row-content">{children}</div>
    </div>
  );
}

/*
|--------------------------------------------------------------------------
| Form Field
|--------------------------------------------------------------------------
*/

function Field({
  label,
  type = "text",
  name,
  value,
  onChange,
  placeholder,
  autoComplete,
}) {
  const { text } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const isPassword = type === "password";

  return (
    <div className="form-field">
      <label htmlFor={name}>{text(label)}</label>

      <div className={isPassword ? "password-input-wrap" : ""}>
        <input
          id={name}
          type={isPassword ? (showPassword ? "text" : "password") : type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={text(placeholder)}
          autoComplete={autoComplete}
        />

        {isPassword && (
          <button
            type="button"
            className="password-show-btn"
            onClick={() => setShowPassword((previous) => !previous)}
            aria-label={`${text(showPassword ? "Hide" : "Show")} ${text(label)}`}
          >
            {text(showPassword ? "Hide" : "Show")}
          </button>
        )}
      </div>
    </div>
  );
}

/*
|--------------------------------------------------------------------------
| Settings Row
|--------------------------------------------------------------------------
*/

function SettingRow({ title, description, checked, onChange }) {
  const { text } = useTranslation();
  return (
    <div className="setting-row">
      <div className="setting-row-copy">
        <h4>{sentenceCaseLabel(text(title))}</h4>

        <p>{text(description)}</p>
      </div>

      <label className="simple-switch">
        <input type="checkbox" checked={checked} onChange={onChange} />

        <span />
      </label>
    </div>
  );
}
